import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomers, createBulkCollections, getCurrentMarketPrice, updateMarketPrice, createCustomer, getUserInfo, getDairyInfo, getWalletBalance, updateDairyInfo } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faArrowLeft,
  faSpinner,
  faCheck,
  faExclamationTriangle,
  faEdit,
  faTimes,
  faSave,
  faEye,
  faStore,
  faUser,
  faPhone,
  faWallet,
  faExclamationCircle,
  faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';
import './ProRataCollection.css';

const ProRataCollection = () => {
  const [customers, setCustomers] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [currentSubmitMessage, setCurrentSubmitMessage] = useState('');
  const [currentRate, setCurrentRate] = useState(null);
  const [isLoadingRate, setIsLoadingRate] = useState(true);
  const [isEditingRate, setIsEditingRate] = useState(false);
  const [customRate, setCustomRate] = useState('');
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [activeCell, setActiveCell] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [previewCollections, setPreviewCollections] = useState([]);
  const [globalDate, setGlobalDate] = useState(new Date().toISOString().split('T')[0]);
  const [globalTime, setGlobalTime] = useState('morning');
  // Add state for deletion confirmation
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  // Add row-specific search state to track search queries for each row
  const [rowSearchQueries, setRowSearchQueries] = useState({});
  const [activeSearchRowIndex, setActiveSearchRowIndex] = useState(null);

  // New state to track dropdown visibility per row
  const [visibleDropdowns, setVisibleDropdowns] = useState({});

  // New state to track the currently selected item in the customer dropdown
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1);

  // State for user and dairy information
  const [userInfo, setUserInfo] = useState(null);
  const [dairyInfo, setDairyInfo] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  // Add state for rate type editing
  const [isEditingRateType, setIsEditingRateType] = useState(false);
  const [rateType, setRateType] = useState('fat_snf'); // Default to fat_snf
  const [submittingRateType, setSubmittingRateType] = useState(false);
  // Add state for pro-rata rate adjustments
  const [fatStepUpRate, setFatStepUpRate] = useState('0'); // Default value
  const [snfStepDownRate, setSnfStepDownRate] = useState('0'); // Default value
  const [loadingInfo, setLoadingInfo] = useState(true);
  // Add state for low balance alert
  const [showLowBalanceAlert, setShowLowBalanceAlert] = useState(false);

  // Add state for fat/SNF ratio type
  const [fatSnfRatio, setFatSnfRatio] = useState('60_40'); // Default to 60% fat, 40% SNF

  const customerInputRefs = useRef([]);
  const latestSearchRequestRef = useRef(0);
  const navigate = useNavigate();

  // New state variables for tracking submission progress
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [submissionTotal, setSubmissionTotal] = useState(0);

  const [newCustomer, setNewCustomer] = useState({
    name: '',
    father_name: '',
    phone: '',
    village: '',
    address: '',
  });

  // Initialize empty rows - converted to useCallback to avoid dependency issues
  const initializeEmptyRows = useCallback((count) => {
    // Only set global date during the first initialization when collections are empty
    if (collections.length === 0) {
      const today = new Date().toISOString().split('T')[0];
      setGlobalDate(today);
    }

    // Use the current rate from state if available, otherwise default to 0
    const rateToUse = currentRate || 0;
    console.log('Initializing rows with rate:', rateToUse);

    // Create fresh collection array instead of updating existing one
    setCollections(() => {
      const timestamp = Date.now();
      return Array.from({ length: count }, (_, index) => ({
        id: `row_${timestamp}_${index}`, // More robust unique id format
        customer: null,
        customerDisplay: '',
        fat_percent: '',
        weight: '',
        snf_percent: '',
        clr: '',
        base_snf: '9.0',
        collection_time: globalTime, // Use global time
        collection_date: globalDate, // Use the current global date
        animal_type: 'cow',
        milk_type: 'cow',
        measured: 'kg',
        liters: 0,
        kg: 0,
        fat_kg: 0,
        snf_kg: 0,
        milk_rate: rateToUse,
        amount: 0,
        isSnfFromClr: false
      }));
    });

    console.log('Initialized', count, 'empty rows');
  }, [currentRate, globalTime, globalDate, collections.length]);

  useEffect(() => {
    // Fetch customers and current market price when component mounts
    fetchCustomers();
    fetchCurrentRate();
  }, []);

  // Initialize rows after rate is loaded
  useEffect(() => {
    // Only initialize rows when rate loading is complete and collections is empty
    if (!isLoadingRate && collections.length === 0) {
      console.log('Initial load: Creating 15 empty rows');
      initializeEmptyRows(15);
    }
  }, [isLoadingRate, currentRate, initializeEmptyRows, collections.length]);

  // Fetch user and dairy information
  useEffect(() => {
    const fetchUserAndDairyInfo = async () => {
      try {
        setLoadingInfo(true);

        // Fetch user information
        const userResponse = await getUserInfo();
        setUserInfo(userResponse);

        // Fetch dairy information
        const dairyResponse = await getDairyInfo();
        setDairyInfo(dairyResponse);

        // Set the rate type from dairy info if available
        if (dairyResponse && dairyResponse.rate_type) {
          setRateType(dairyResponse.rate_type);
        }

        // Fetch wallet balance with improved debugging
        try {
          const walletResponse = await getWalletBalance();
          console.log('Wallet API response:', walletResponse);

          let balance = null;

          // Check different possible properties for balance
          if (walletResponse && walletResponse.balance !== undefined) {
            balance = walletResponse.balance;
          } else if (walletResponse && walletResponse.amount !== undefined) {
            balance = walletResponse.amount;
          } else if (walletResponse && typeof walletResponse === 'number') {
            // In case the response is just the balance as a number
            balance = walletResponse;
          } else if (walletResponse && typeof walletResponse === 'object') {
            // Log all available properties
            console.log('Available wallet properties:', Object.keys(walletResponse));
            // Try to find a property that might contain the balance
            const possibleBalanceKeys = ['balance', 'amount', 'wallet_balance', 'value', 'total'];
            for (const key of possibleBalanceKeys) {
              if (walletResponse[key] !== undefined) {
                console.log(`Found balance in property: ${key} with value: ${walletResponse[key]}`);
                balance = walletResponse[key];
                break;
              }
            }
          }

          setWalletBalance(balance);

          // Check if balance is below threshold (50 rupees)
          if (balance !== null && balance !== undefined && parseFloat(balance) < 50) {
            setShowLowBalanceAlert(true);
          }
        } catch (error) {
          console.error('Error fetching wallet balance:', error);
        }
      } catch (error) {
        console.error('Error fetching user, dairy information, or wallet balance:', error);
      } finally {
        setLoadingInfo(false);
      }
    };

    fetchUserAndDairyInfo();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomers();
      setCustomers(response.results || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentRate = async () => {
    try {
      setIsLoadingRate(true);
      const response = await getCurrentMarketPrice();
      if (response && response.price) {
        setCurrentRate(response.price);
        setCustomRate(response.price.toString()); // Initialize custom rate with current rate
      }
    } catch (error) {
      setCurrentRate(0);
      setCustomRate('0');
      console.error('Error fetching current rate:', error);
    } finally {
      setIsLoadingRate(false);
    }
  };

  // Calculate SNF from CLR
  const calculateSnfFromClr = (clrValue, fatValue) => {
    // Ensure we have valid inputs
    if (!clrValue || !fatValue) {
      return 0;
    }

    // Convert strings to numbers to ensure proper calculation
    const clr = parseFloat(clrValue);
    const fat = parseFloat(fatValue);

    // Additional validation for reasonable values
    if (isNaN(clr) || isNaN(fat) || clr <= 0 || fat <= 0) {
      return 0;
    }

    // SNF calculation formula: SNF = (CLR / 4) + (0.20 * FAT) + 0.14
    const calculatedSnf = Math.floor(((clr / 4) + (fat * 0.20) + 0.14) * 100) / 100;

    // Format to exactly 2 decimal places and ensure it matches the step of 0.01
    // Round to nearest 0.01 using Math.round to avoid browser validation issues
    //return Math.floor(calculatedSnf * 100) / 100;
    return calculatedSnf;
  };

  // Calculate weight, fat_kg, snf_kg, and amount based on input values
  const calculateDerivedValues = (collection) => {
    const fat = parseFloat(collection.fat_percent) || 0;
    const snf = parseFloat(collection.snf_percent) || 0;
    const weight = parseFloat(collection.weight) || 0;
    const rate = parseFloat(collection.milk_rate) || currentRate || 0; // Use collection-specific rate
    const baseSnf = parseFloat(collection.base_snf) || 9.0;

    // Calculate fat_kg and snf_kg (same for both calculation methods)
    const fatKg = Math.floor((weight * (fat / 100)) * 100) / 100;
    const snfKg = Math.floor((weight * (snf / 100)) * 100) / 100;

    // Get fat/SNF ratio percentages based on selected option
    const fatRatioPercent = fatSnfRatio === '60_40' ? 60 : 52;
    const snfRatioPercent = fatSnfRatio === '60_40' ? 40 : 48;

    // Use the dynamic ratio percentages in the calculation
    const fatRate = Math.floor((rate * fatRatioPercent / 6.5) * 100) / 100;
    const snfRate = Math.floor((rate * snfRatioPercent / baseSnf) * 100) / 100;

    // Result variables
    let finalRate = rate;
    let amount = 0;

    // Pro-rata calculation logic when fat% > 6.5
    if (fat > 6.5) {
      // Parse the step rates
      const fatStepUpRateValue = (parseFloat(fatStepUpRate) * 10) || 0;
      const snfStepDownRateValue = (parseFloat(snfStepDownRate) * 10) || 0;

      // Calculate pro-rata adjustments
      const fatAdjustment = (fat - 6.5) * fatStepUpRateValue;
      const snfAdjustment = (snf - 9.0) * snfStepDownRateValue;

      // Calculate final rate with pro-rata adjustments
      finalRate = rate + fatAdjustment + snfAdjustment;

      // Calculate amount using the pro-rata rate
      amount = Math.floor((finalRate * weight) * 100) / 100;
    }
    // Original calculation logic for fat% <= 6.5
    else {
      // Calculate amount based on fat, snf rates and components
      amount = Math.floor(parseFloat(fatKg) * parseFloat(fatRate) * 100) / 100 +
        Math.floor(parseFloat(snfKg) * parseFloat(snfRate) * 100) / 100;
    }

    // Calculate solid weight (same for both methods)
    const solidWeight = (amount / rate).toFixed(3);

    return {
      fat_kg: fatKg,
      snf_kg: snfKg,
      fat_rate: fatRate,
      snf_rate: snfRate,
      solid_weight: solidWeight,
      amount: Math.round(amount), // Round to nearest integer
      finalRate: finalRate // Return the final calculated rate
    };
  };

  // Add multiple rows at once
  const addCollection = (count = 5) => {
    // Get animal type from the most recent collection
    const mostRecentAnimalType = collections.length > 0
      ? collections[collections.length - 1].animal_type
      : 'cow';

    // Get base SNF from the most recent collection
    const mostRecentBaseSnf = collections.length > 0
      ? collections[collections.length - 1].base_snf
      : '9.0';

    // Use current rate from state
    const rateToUse = currentRate || 0;

    // Use functional update to ensure we're working with the latest state
    setCollections(prevCollections => {
      const timestamp = Date.now();
      const newCollections = Array.from({ length: count }, (_, index) => ({
        id: `row_${timestamp}_${index}`, // Ensuring unique IDs with timestamp
        customer: null,
        customerDisplay: '',
        fat_percent: '',
        weight: '',
        snf_percent: '',
        clr: '',
        base_snf: mostRecentBaseSnf,
        collection_time: globalTime,
        collection_date: globalDate,
        animal_type: mostRecentAnimalType,
        milk_type: mostRecentAnimalType,
        measured: 'kg',
        liters: 0,
        kg: 0,
        fat_kg: 0,
        snf_kg: 0,
        milk_rate: rateToUse,
        amount: 0,
        isSnfFromClr: false
      }));

      return [...prevCollections, ...newCollections];
    });

    console.log('Adding', count, 'rows to collections');
  };

  // Remove a collection row
  const removeCollection = (id) => {
    console.log('Attempting to remove collection with id:', id);
    console.log('Current collections before removal:', collections);

    // Use functional update to ensure we have the latest state
    setCollections(prevCollections => {
      console.log('Previous collections in functional update:', prevCollections);
      const filtered = prevCollections.filter(c => c.id !== id);
      console.log('Filtered collections after removal:', filtered);
      return filtered;
    });

    // Clear any form errors for this collection
    if (formErrors[id]) {
      setFormErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[id];
        return newErrors;
      });
    }

    console.log('Removal function completed for id:', id);
  };

  // Helper function to check if a row has any data entered
  const rowHasData = (collection) => {
    // Check if any important fields have data
    return !!(
      collection.customer ||
      collection.customerDisplay ||
      collection.fat_percent ||
      collection.weight ||
      collection.snf_percent ||
      collection.clr
    );
  };

  // Handle the delete button click
  const handleDeleteClick = (collection) => {
    // Check if row has data
    if (rowHasData(collection)) {
      // Show confirmation dialog if row has data
      setRowToDelete(collection.id);
      setShowDeleteConfirmation(true);
    } else {
      // Remove immediately if row is empty
      removeCollection(collection.id);
    }
  };

  // Handle customer search - update to use row-specific search queries
  const handleSearch = useCallback(async (rowIndex, query) => {
    const trimmedQuery = query.trim();

    // Update the row-specific search query
    setRowSearchQueries(prev => ({
      ...prev,
      [rowIndex]: query
    }));

    // Track the active search row
    setActiveSearchRowIndex(rowIndex);

    // Show the customer list if the query has content - make this row-specific
    setVisibleDropdowns(prev => ({
      ...prev,
      [rowIndex]: trimmedQuery.length > 0
    }));

    // Set the current collection for potential new customer creation
    setCurrentCollection(collections[rowIndex] || null);

    if (!trimmedQuery) {
      latestSearchRequestRef.current = Date.now();
      setFilteredCustomers([]);
      setSelectedCustomerIndex(-1);
      return;
    }

    const requestId = Date.now();
    latestSearchRequestRef.current = requestId;

    const params = {
      search: trimmedQuery,
      page_size: 50
    };

    if (/^\d+$/.test(trimmedQuery)) {
      params.customer_id = trimmedQuery;
    }

    try {
      const response = await getCustomers(params);
      if (latestSearchRequestRef.current !== requestId) {
        return;
      }

      const customerData = response?.results ?? (Array.isArray(response) ? response : []);
      const normalizedCustomers = customerData.map(customer => ({
        ...customer,
        customer_id: customer.customer_id ?? customer.id
      }));

      setFilteredCustomers(normalizedCustomers);
      setSelectedCustomerIndex(normalizedCustomers.length > 0 ? 0 : -1);

      setCustomers(prev => {
        if (normalizedCustomers.length === 0) {
          return prev;
        }

        const existingIds = new Set(prev.map(item => item.id ?? item.customer_id));
        const merged = [...prev];

        normalizedCustomers.forEach(customer => {
          const key = customer.id ?? customer.customer_id;
          if (!existingIds.has(key)) {
            merged.push(customer);
            existingIds.add(key);
          }
        });

        return merged;
      });
    } catch (error) {
      console.error('Error searching customers:', error);

      if (latestSearchRequestRef.current !== requestId) {
        return;
      }

      const loweredQuery = trimmedQuery.toLowerCase();
      const fallbackResults = customers.filter(customer => {
        const idString = customer.customer_id?.toString() || '';
        const normalizedName = customer.name?.toLowerCase() || '';
        return idString.includes(loweredQuery) || normalizedName.includes(loweredQuery);
      });

      setFilteredCustomers(fallbackResults);
      setSelectedCustomerIndex(fallbackResults.length > 0 ? 0 : -1);
    }
  }, [collections, customers]);

  // Handle customer selection - updated for clarity
  const handleSelectCustomer = (rowIndex, customer) => {
    const updatedCollections = [...collections];
    updatedCollections[rowIndex] = {
      ...updatedCollections[rowIndex],
      customer: customer,
      customerDisplay: `${customer.customer_id} - ${customer.name}`
    };

    setCollections(updatedCollections);

    // Clear search state for this row
    setRowSearchQueries(prev => ({
      ...prev,
      [rowIndex]: ''
    }));

    // Hide dropdown for this specific row
    setVisibleDropdowns(prev => ({
      ...prev,
      [rowIndex]: false
    }));

    setActiveSearchRowIndex(null);
    // Reset the selected customer index
    setSelectedCustomerIndex(-1);

    // Focus on the next cell (weight)
    if (collections.length > rowIndex) {
      const weightCell = document.getElementById(`weight-${rowIndex}`);
      if (weightCell) {
        weightCell.focus();
      }
    }
  };

  // Handle cell focus - updated to support search
  const handleCellFocus = (rowIndex, cellName) => {
    setActiveCell({ rowIndex, cellName });

    // If focusing on a customer cell, set the active search row
    if (cellName === 'customer') {
      setActiveSearchRowIndex(rowIndex);

      // Initialize search query for this row if it doesn't exist
      if (!rowSearchQueries[rowIndex] && !collections[rowIndex].customerDisplay) {
        setRowSearchQueries(prev => ({
          ...prev,
          [rowIndex]: ''
        }));
      } else if (collections[rowIndex].customerDisplay && !rowSearchQueries[rowIndex]) {
        // When focusing on a cell with an existing customer but no search query,
        // initialize the search query as empty to prevent display issues
        setRowSearchQueries(prev => ({
          ...prev,
          [rowIndex]: ''
        }));
      }

      // Show customer list if there's an active search query
      if (rowSearchQueries[rowIndex] && rowSearchQueries[rowIndex].length > 0) {
        handleSearch(rowIndex, rowSearchQueries[rowIndex]);
        // The handleSearch will set selectedCustomerIndex appropriately
      } else {
        // Reset selected index if no search in progress
        setSelectedCustomerIndex(-1);
      }
    } else {
      // If focusing on non-customer cell, hide the dropdown
      setVisibleDropdowns(prev => ({
        ...prev,
        [rowIndex]: false
      }));
      // Reset selected customer index
      setSelectedCustomerIndex(-1);
    }
  };

  // Handle cell blur event - process after user leaves a cell
  const handleCellBlur = (rowIndex, field, value) => {
    // Skip empty values
    if (!value || value.trim() === '') return;

    // Special handling for fat_percent
    if (field === 'fat_percent') {
      // Try to convert the value if needed
      const processedValue = processDecimalInput(value, 'fat');

      if (processedValue !== value) {
        // Update the collection with the processed value
        setCollections(collections.map((col, idx) => {
          if (idx === rowIndex) {
            return { ...col, fat_percent: processedValue };
          }
          return col;
        }));
      }

      // Validate the fat percentage is within acceptable range
      const numValue = parseFloat(processedValue);
      if (!isNaN(numValue)) {
        setFormErrors(prev => {
          const newErrors = { ...prev };

          // Clear any previous error for this field
          if (newErrors[collections[rowIndex].id]) {
            delete newErrors[collections[rowIndex].id].fat_percent;
          }

          // If no errors remain for this collection, remove the collection entry
          if (newErrors[collections[rowIndex].id] &&
            Object.keys(newErrors[collections[rowIndex].id]).length === 0) {
            delete newErrors[collections[rowIndex].id];
          }

          return newErrors;
        });
      }
    }

    // Special handling for snf_percent
    else if (field === 'snf_percent' && !collections[rowIndex].isSnfFromClr) {
      // Try to convert the value if needed
      const processedValue = processDecimalInput(value, 'snf');

      if (processedValue !== value) {
        // Update the collection with the processed value
        setCollections(collections.map((col, idx) => {
          if (idx === rowIndex) {
            return { ...col, snf_percent: processedValue };
          }
          return col;
        }));
      }

      // Validate the SNF percentage is within acceptable range
      const numValue = parseFloat(processedValue);
      if (!isNaN(numValue)) {
        setFormErrors(prev => {
          const newErrors = { ...prev };

          // Clear any previous error for this field
          if (newErrors[collections[rowIndex].id]) {
            delete newErrors[collections[rowIndex].id].snf_percent;
          }

          // If no errors remain for this collection, remove the collection entry
          if (newErrors[collections[rowIndex].id] &&
            Object.keys(newErrors[collections[rowIndex].id]).length === 0) {
            delete newErrors[collections[rowIndex].id];
          }

          return newErrors;
        });
      }
    }

    // Special handling for CLR
    else if (field === 'clr') {
      // Try to convert the value if needed
      const processedValue = processDecimalInput(value, 'clr');

      if (processedValue !== value) {
        // Update the collection with the processed value
        setCollections(collections.map((col, idx) => {
          if (idx === rowIndex) {
            return { ...col, clr: processedValue };
          }
          return col;
        }));
      }

      // Validate the CLR value is within acceptable range (13.1 to 34.0)
      const numValue = parseFloat(processedValue);
      if (!isNaN(numValue)) {
        setFormErrors(prev => {
          const newErrors = { ...prev };

          // Check if CLR is within acceptable range
          if (numValue < 13.1 || numValue > 34.0) {
            // Ensure the collection has an errors object
            if (!newErrors[collections[rowIndex].id]) {
              newErrors[collections[rowIndex].id] = {};
            }

            // Set error message based on range violation
            if (numValue < 13.1) {
              newErrors[collections[rowIndex].id].clr = `CLR value ${numValue} is below the minimum (13.1)`;
            } else {
              newErrors[collections[rowIndex].id].clr = `CLR value ${numValue} is above the maximum (34.0)`;
            }
          } else {
            // Clear any previous error for this field
            if (newErrors[collections[rowIndex].id]) {
              delete newErrors[collections[rowIndex].id].clr;
            }

            // If no errors remain for this collection, remove the collection entry
            if (newErrors[collections[rowIndex].id] &&
              Object.keys(newErrors[collections[rowIndex].id]).length === 0) {
              delete newErrors[collections[rowIndex].id];
            }
          }

          return newErrors;
        });
      }

      // If we have both CLR and fat, recalculate SNF
      if (collections[rowIndex].fat_percent) {
        // Calculate SNF from CLR and fat
        const snfValue = calculateSnfFromClr(processedValue, collections[rowIndex].fat_percent);

        // Update the collection with the calculated SNF value
        setCollections(collections.map((col, idx) => {
          if (idx === rowIndex) {
            return {
              ...col,
              clr: processedValue, // Ensure processed CLR value is stored
              snf_percent: snfValue,
              isSnfFromClr: true
            };
          }
          return col;
        }));
      }
    }

    // For weight or price_per_unit, recalculate derived values if we have necessary inputs
    else if ((field === 'weight' || field === 'price_per_unit') &&
      collections[rowIndex].fat_percent &&
      ((rateType === 'fat_snf' && collections[rowIndex].snf_percent) ||
        (rateType === 'fat_clr' && collections[rowIndex].clr))) {

      // Recalculate derived values for this collection
      const updatedCollection = { ...collections[rowIndex], [field]: value };
      const derived = calculateDerivedValues(updatedCollection);

      // Update the collection with calculated values
      setCollections(collections.map((col, idx) => {
        if (idx === rowIndex) {
          return {
            ...col,
            fat_kg: derived.fat_kg,
            snf_kg: derived.snf_kg,
            amount: derived.amount
          };
        }
        return col;
      }));
    }
  };

  // Helper function to process decimal input for fat, snf and clr fields
  const processDecimalInput = (value, type) => {
    // If it's already in decimal format with a decimal point, return as is
    if (value.includes('.')) return value;

    // Convert whole numbers to decimal format
    if (/^\d+$/.test(value)) {
      // For fat and snf, we divide by 10 (e.g., 35 -> 3.5)
      if (type === 'fat' || type === 'snf') {
        return (parseInt(value, 10) / 10).toFixed(1);
      }
      // For CLR, we divide by 10 (e.g., 280 -> 28.0, 20 -> 2.0)
      else if (type === 'clr') {
        // Only process numbers that are likely supposed to be CLR values (above 100)
        const numValue = parseInt(value, 10);
        // If it's a two-digit or three-digit number, divide by 10 (e.g., 20 -> 2.0, 280 -> 28.0)
        if (numValue >= 10) {
          return (numValue / 10).toFixed(1);
        }
        // For single-digit values, add a decimal (e.g., 9 -> 9.0)
        else {
          return numValue.toFixed(1);
        }
      }
    }

    return value;
  };

  // Handle input changes for any cell
  const handleInputChange = (rowIndex, field, value) => {
    // Handle customer search input separately
    if (field === 'customer') {
      handleSearch(rowIndex, value);
      return;
    }

    setCollections(collections.map((col, idx) => {
      if (idx === rowIndex) {
        const updatedCollection = { ...col };

        // Special handling for fat_percent input
        if (field === 'fat_percent') {
          if (value === '') {
            updatedCollection.fat_percent = value;
          } else {
            // For whole numbers, just validate they're numeric
            if (/^\d+$/.test(value)) {
              updatedCollection.fat_percent = value;
            }
            // For decimal values, validate format (up to 1 decimal place)
            else if (/^\d*\.?\d{0,1}$/.test(value)) {
              updatedCollection.fat_percent = value;
            } else {
              return col; // Invalid input, don't update
            }
          }
        }

        // Special handling for snf_percent input
        else if (field === 'snf_percent' && rateType === 'fat_snf') {
          if (value === '') {
            updatedCollection.snf_percent = value;
          } else {
            // For whole numbers, just validate they're numeric
            if (/^\d+$/.test(value)) {
              updatedCollection.snf_percent = value;
            }
            // For decimal values, validate format (up to 1 decimal place)
            else if (/^\d*\.?\d{0,1}$/.test(value)) {
              updatedCollection.snf_percent = value;
            } else {
              return col; // Invalid input, don't update
            }
          }
        }

        // Special handling for CLR input
        else if (field === 'clr' && rateType === 'fat_clr') {
          if (value === '') {
            updatedCollection.clr = value;

            // Clear any existing CLR errors for this row
            if (formErrors[col.id]?.clr) {
              setFormErrors(prev => {
                const newErrors = { ...prev };
                if (newErrors[col.id]) {
                  delete newErrors[col.id].clr;
                  if (Object.keys(newErrors[col.id]).length === 0) {
                    delete newErrors[col.id];
                  }
                }
                return newErrors;
              });
            }
          } else {
            // For whole numbers, just validate they're numeric
            if (/^\d+$/.test(value)) {
              updatedCollection.clr = value;
            }
            // For decimal values, validate format (up to 2 decimal places)
            else if (/^\d*\.?\d{0,2}$/.test(value)) {
              updatedCollection.clr = value;
            } else {
              return col; // Invalid input, don't update
            }

            // Check for valid CLR range if we have a complete number
            if (value && !value.endsWith('.')) {
              const clrValue = parseFloat(value);
              if (!isNaN(clrValue)) {
                if (clrValue < 13.1 || clrValue > 34.0) {
                  // Set error for CLR value outside acceptable range
                  const errorMessage = clrValue < 13.1 ?
                    `CLR value ${clrValue} is below the minimum (13.1)` :
                    `CLR value ${clrValue} is above the maximum (34.0)`;

                  setFormErrors(prev => {
                    const newErrors = { ...prev };
                    if (!newErrors[col.id]) {
                      newErrors[col.id] = {};
                    }
                    newErrors[col.id].clr = errorMessage;
                    return newErrors;
                  });
                } else {
                  // Clear the error if value is now valid
                  if (formErrors[col.id]?.clr) {
                    setFormErrors(prev => {
                      const newErrors = { ...prev };
                      if (newErrors[col.id]) {
                        delete newErrors[col.id].clr;
                        if (Object.keys(newErrors[col.id]).length === 0) {
                          delete newErrors[col.id];
                        }
                      }
                      return newErrors;
                    });
                  }
                }
              }
            }
          }

          // If we have both fat and CLR, calculate SNF
          if (value && updatedCollection.fat_percent) {
            const snfValue = calculateSnfFromClr(value, updatedCollection.fat_percent);
            updatedCollection.snf_percent = snfValue;
            updatedCollection.isSnfFromClr = true;
          }
        }
        // All other fields
        else {
          updatedCollection[field] = value;
        }

        // If we've updated weight or rate, recalculate derived values
        if ((field === 'weight' || field === 'price_per_unit') && col.fat_percent &&
          ((rateType === 'fat_snf' && col.snf_percent) ||
            (rateType === 'fat_clr' && col.clr))) {

          const derived = calculateDerivedValues({
            ...col,
            [field]: value
          });

          updatedCollection.fat_kg = derived.fat_kg;
          updatedCollection.snf_kg = derived.snf_kg;
          updatedCollection.amount = derived.amount;
        }

        return updatedCollection;
      }
      return col;
    }));
  };

  // Handle key navigation in the grid
  const handleKeyDown = (e, rowIndex, cellName) => {
    // Updated cells array to include date and time
    const cells = ['collection_date', 'collection_time', 'customer', 'weight', 'fat_percent', 'snf_percent', 'clr', 'milk_rate', 'base_snf', 'animal_type'];
    const currentIndex = cells.indexOf(cellName);

    // Helper function to get the next available cell index (skipping disabled fields)
    const getNextCellIndex = (currentIdx, direction = 1) => {
      let nextIdx = currentIdx + direction;

      // Loop until we find an enabled cell or reach the end/beginning
      while (nextIdx >= 0 && nextIdx < cells.length) {
        const cellName = cells[nextIdx];

        // Skip SNF field if rate type is fat_clr
        if (cellName === 'snf_percent' && rateType === 'fat_clr') {
          nextIdx += direction;
          continue;
        }

        // Skip CLR field if rate type is fat_snf
        if (cellName === 'clr' && rateType === 'fat_snf') {
          nextIdx += direction;
          continue;
        }

        // Found an enabled cell
        return nextIdx;
      }

      // If we went past the array boundaries, return the boundary
      return direction > 0 ? cells.length - 1 : 0;
    };

    // Handle customer dropdown navigation when dropdown is visible
    if (cellName === 'customer' && visibleDropdowns[rowIndex] && filteredCustomers.length > 0) {
      // Arrow down - move to next customer in dropdown
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCustomerIndex(prevIndex => {
          const newIndex = prevIndex < filteredCustomers.length - 1 ? prevIndex + 1 : 0;
          return newIndex;
        });
        return;
      }
      // Arrow up - move to previous customer in dropdown
      else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCustomerIndex(prevIndex => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : filteredCustomers.length - 1;
          return newIndex;
        });
        return;
      }
      // Enter - select the currently highlighted customer
      else if (e.key === 'Enter' && selectedCustomerIndex >= 0) {
        e.preventDefault();
        const selectedCustomer = filteredCustomers[selectedCustomerIndex];
        if (selectedCustomer) {
          handleSelectCustomer(rowIndex, selectedCustomer);
          setSelectedCustomerIndex(-1);
          return;
        }
      }
    }

    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();

      // Move to the next cell in the row, skipping disabled fields
      if (currentIndex < cells.length - 1) {
        const nextCellIndex = getNextCellIndex(currentIndex);
        if (nextCellIndex < cells.length) {
          const nextCell = document.getElementById(`${cells[nextCellIndex]}-${rowIndex}`);
          if (nextCell) {
            nextCell.focus();
          }
        }
      }
      // Move to the first cell in the next row
      else if (rowIndex < collections.length - 1) {
        const nextCell = document.getElementById(`${cells[0]}-${rowIndex + 1}`);
        if (nextCell) {
          nextCell.focus();
        }
      }
      // Add a new row if we're at the last cell of the last row
      else if (rowIndex === collections.length - 1) {
        addCollection(1);
      }
    }
    else if (e.key === 'ArrowDown' && !visibleDropdowns[rowIndex]) {
      e.preventDefault();
      // Move to the same cell in the next row
      if (rowIndex < collections.length - 1) {
        const nextCell = document.getElementById(`${cellName}-${rowIndex + 1}`);
        if (nextCell) {
          nextCell.focus();
        }
      }
    }
    else if (e.key === 'ArrowUp' && !visibleDropdowns[rowIndex]) {
      e.preventDefault();
      // Move to the same cell in the previous row
      if (rowIndex > 0) {
        const prevCell = document.getElementById(`${cellName}-${rowIndex - 1}`);
        if (prevCell) {
          prevCell.focus();
        }
      }
    }
    else if (e.key === 'ArrowRight' && currentIndex < cells.length - 1) {
      e.preventDefault();
      // Move to the next cell in the row, skipping disabled fields
      const nextCellIndex = getNextCellIndex(currentIndex);
      if (nextCellIndex < cells.length) {
        const nextCell = document.getElementById(`${cells[nextCellIndex]}-${rowIndex}`);
        if (nextCell) {
          nextCell.focus();
        }
      }
    }
    else if (e.key === 'ArrowLeft' && currentIndex > 0) {
      e.preventDefault();
      // Move to the previous cell in the row, skipping disabled fields
      const prevCellIndex = getNextCellIndex(currentIndex, -1);
      if (prevCellIndex >= 0) {
        const prevCell = document.getElementById(`${cells[prevCellIndex]}-${rowIndex}`);
        if (prevCell) {
          prevCell.focus();
        }
      }
    }
  };

  // Validate a single collection
  const validateCollection = (collection, index) => {
    const errors = {};

    if (!collection.customer || !collection.customer.id) {
      errors.customer = 'Customer is required';
    }

    if (!collection.fat_percent) {
      errors.fat_percent = 'Fat percent is required';
    } else if (isNaN(collection.fat_percent) || parseFloat(collection.fat_percent) <= 0) {
      errors.fat_percent = 'Valid fat percent is required';
    } else if (parseFloat(collection.fat_percent) < 2.0) {
      errors.fat_percent = 'Fat percent must be at least 2.0%';
    } else if (parseFloat(collection.fat_percent) > 13.1) {
      errors.fat_percent = 'Fat percent cannot exceed 13.1%';
    }

    if (!collection.weight) {
      errors.weight = 'Weight is required';
    } else if (isNaN(collection.weight) || parseFloat(collection.weight) <= 0) {
      errors.weight = 'Valid weight is required';
    }

    // Validate based on rate type
    if (rateType === 'fat_snf') {
      // For fat_snf rate type, validate SNF
      if (!collection.snf_percent) {
        errors.snf_percent = 'SNF percent is required';
      } else if (isNaN(collection.snf_percent) || parseFloat(collection.snf_percent) <= 0) {
        errors.snf_percent = 'Valid SNF percent is required';
      } else if (parseFloat(collection.snf_percent) < 4.0) {
        errors.snf_percent = 'SNF percent must be at least 4.0%';
      } else if (parseFloat(collection.snf_percent) > 11.0) {
        errors.snf_percent = 'SNF percent cannot exceed 11.0%';
      }
    } else if (rateType === 'fat_clr') {
      // For fat_clr rate type, validate CLR
      if (!collection.clr) {
        errors.clr = 'CLR is required';
      } else if (isNaN(collection.clr) || parseFloat(collection.clr) <= 0) {
        errors.clr = 'Valid CLR is required';
      } else {
        // Check if CLR value is within acceptable range
        const clrValue = parseFloat(collection.clr);
        if (clrValue < 10) {
          errors.clr = `CLR value "${collection.clr}" is invalid. Must have at least 2 digits (e.g., 27.50)`;
        } else if (clrValue < 13.1) {
          errors.clr = `CLR value ${clrValue} is below the minimum (13.1)`;
        } else if (clrValue > 34.0) {
          errors.clr = `CLR value ${clrValue} is above the maximum (34.0)`;
        }
      }
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  // Validate all collections
  const validateAllCollections = () => {
    if (collections.length === 0) {
      setSubmitError('Please add at least one collection');
      return false;
    }

    const newErrors = {};
    let hasErrors = false;

    collections.forEach((collection, index) => {
      const collectionErrors = validateCollection(collection, index);
      if (collectionErrors) {
        newErrors[collection.id] = collectionErrors;
        hasErrors = true;
      }
    });

    setFormErrors(newErrors);
    return !hasErrors;
  };

  // Format collections for API submission
  const formatCollectionsForSubmission = () => {
    return collections.map(collection => {
      // Recalculate derived values to ensure accuracy
      const derived = calculateDerivedValues(collection);

      // Get fat and snf values
      const fat = parseFloat(collection.fat_percent) || 0;
      const snf = collection.snf_percent; //parseFloat(collection.snf_percent) || 0;
      const weight = parseFloat(collection.weight) || 0;
      const milkRate = parseFloat(collection.milk_rate) || 0;

      const liters = Math.floor((weight / 1.02249) * 100) / 100;

      // Calculate fat_kg and snf_kg
      const fatKg = Math.floor((weight * (fat / 100)) * 100) / 100;
      const snfKg = Math.floor((weight * (snf / 100)) * 100) / 100;

      // Format milk_type correctly for API - use cow_buffalo instead of mixed
      const milk_type = collection.animal_type === 'cow+buffalo' ? 'cow_buffalo' : collection.animal_type;

      // Rate cal for fat less than 6.5
      const newRate = derived.amount / weight

      // Use the pro-rata adjusted rate if fat% > 6.5
      const effectiveRate = fat > 6.5 ? derived.finalRate : newRate;

      // Format the request payload to match the expected format exactly
      return {
        customer: collection.customer.id,
        collection_time: collection.collection_time,
        collection_date: collection.collection_date,
        animal_type: collection.animal_type,
        milk_type: milk_type,
        measured: 'kg',
        fat_percentage: fat,
        snf_percentage: snf,
        base_snf: collection.base_snf, // Include base SNF
        clr: parseFloat(collection.clr) || 0,  // Include CLR value
        weight: weight,
        kg: weight,
        liters: liters,
        fat_kg: fatKg,
        snf_kg: snfKg,
        fat_rate: derived.fat_rate,
        snf_rate: derived.snf_rate,
        milk_rate: milkRate, //parseFloat(Math.floor(effectiveRate * 100) / 100), // CHANGED: Now sending the effective rate as the milk_rate
        //base_rate: milkRate, // Store the original base rate as base_rate
        //effective_rate: effectiveRate, // Keep the effective rate for reference
        fat_step_up_rate: parseFloat(fatStepUpRate) || 0, // Include fat step up rate
        snf_step_down_rate: parseFloat(snfStepDownRate) || 0, // Include SNF step down rate
        is_pro_rata: true, // Flag to indicate if pro-rata calculation was used
        amount: derived.amount,
        solid_weight: derived.solid_weight
      };
    });
  };

  // Handle preview before submission
  const handlePreview = (e) => {
    e.preventDefault();

    // Validate all collections first
    if (!validateAllCollections()) {
      // Find collections with errors (prioritize fat_percent and snf_percent errors first)
      const fatErrorCollection = collections.find(collection =>
        formErrors[collection.id]?.fat_percent
      );

      const snfErrorCollection = collections.find(collection =>
        formErrors[collection.id]?.snf_percent
      );

      const clrErrorCollection = collections.find(collection =>
        formErrors[collection.id]?.clr
      );

      // Scroll to and focus the first error field we find, prioritizing fat and snf
      if (fatErrorCollection) {
        // Show error message in the UI
        setSubmitError(formErrors[fatErrorCollection.id].fat_percent);
        // Scroll to the row with error
        const errorRow = document.getElementById(`fat_percent-${collections.indexOf(fatErrorCollection)}`);
        if (errorRow) {
          errorRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorRow.focus();
        }
      } else if (snfErrorCollection) {
        // Show error message in the UI
        setSubmitError(formErrors[snfErrorCollection.id].snf_percent);
        // Scroll to the row with error
        const errorRow = document.getElementById(`snf_percent-${collections.indexOf(snfErrorCollection)}`);
        if (errorRow) {
          errorRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorRow.focus();
        }
      } else if (clrErrorCollection) {
        // Show error message in the UI
        setSubmitError(formErrors[clrErrorCollection.id].clr);
        // Scroll to the row with error
        const errorRow = document.getElementById(`clr-${collections.indexOf(clrErrorCollection)}`);
        if (errorRow) {
          errorRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorRow.focus();
        }
      } else {
        // Find any other error if no fat/snf/clr errors
        const firstErrorCollection = collections.find(collection => formErrors[collection.id]);
        if (firstErrorCollection) {
          // Get the first error field
          const firstErrorField = Object.keys(formErrors[firstErrorCollection.id])[0];
          // Show error message in the UI
          setSubmitError(formErrors[firstErrorCollection.id][firstErrorField]);
          // Attempt to scroll to the row with error
          const rowIndex = collections.indexOf(firstErrorCollection);
          const errorRow = document.getElementById(`${firstErrorField}-${rowIndex}`);
          if (errorRow) {
            errorRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorRow.focus();
          }
        }
      }
      return;
    }

    // Generate preview data
    const formattedCollections = formatCollectionsForSubmission();
    setPreviewCollections(formattedCollections);
    setShowPreviewModal(true);
  };

  // Handle final submission
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    setShowPreviewModal(false);

    try {
      const formattedCollections = formatCollectionsForSubmission();

      // Log formatted collections for debugging
      console.log('Formatted collections for API:', formattedCollections);

      // Additional validation before submission
      const invalidCollections = formattedCollections.filter(
        c => !c.customer || !c.fat_percentage || !c.snf_percentage || !c.weight
      );

      if (invalidCollections.length > 0) {
        console.error('Invalid collections detected:', invalidCollections);
        setSubmitError('Some collections have invalid data. Please check all fields and try again.');
        setSubmitting(false);
        return;
      }

      // Submit collections one by one
      let successCount = 0;
      const originalCollections = [...collections]; // Keep a copy for reference

      // Set initial progress tracking
      setSubmissionTotal(formattedCollections.length);
      setSubmissionProgress(0);

      for (let i = 0; i < formattedCollections.length; i++) {
        try {
          // Submit a single collection
          await createBulkCollections([formattedCollections[i]]);

          // Increment success counter
          successCount++;

          // Update progress
          setSubmissionProgress(successCount);

          // Remove the submitted collection from the state
          setCollections(prevCollections => {
            // Find the collection with the matching customer ID and remove it
            const collectionToRemove = originalCollections[i];
            return prevCollections.filter(c => c.id !== collectionToRemove.id);
          });

          // Show success message
          setCurrentSubmitMessage(`Collection for ${originalCollections[i].customerDisplay || 'customer'} submitted successfully!`);
          setSubmitSuccess(true);
          // Keep success message visible for a short time
          setTimeout(() => {
            setSubmitSuccess(false);
          }, 1000);

        } catch (error) {
          console.error(`Error submitting collection ${i + 1}:`, error);
          setSubmitError(`Failed to submit collection for ${originalCollections[i].customerDisplay || 'customer'}: ${error.error || 'Unknown error'}`);
          break; // Stop on first error
        }
      }

      if (successCount === formattedCollections.length) {
        // All collections were submitted successfully
        setSubmitError('');
        setSubmitSuccess(true);
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 3000);
      }

    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError(error.error || 'Failed to submit collections. Please try again.');
    } finally {
      setSubmitting(false);
      // Reset progress tracking
      setSubmissionProgress(0);
      setSubmissionTotal(0);
    }
  };

  // Go back to dashboard
  const goBack = () => {
    navigate('/dashboard');
  };

  // Handle rate edit toggle
  const toggleRateEdit = async () => {
    if (isEditingRate) {
      // Save the edited rate if it's valid
      const parsedRate = parseFloat(customRate);
      if (!isNaN(parsedRate) && parsedRate >= 0) {
        try {
          setSubmitting(true);
          // Call API to update the rate in the backend
          await updateMarketPrice(parsedRate);
          setCurrentRate(parsedRate);
          // Update all collection amounts with the new rate
          updateCollectionAmounts(parsedRate);
        } catch (error) {
          console.error('Error updating milk rate:', error);
          setSubmitError(error.error || 'Failed to update milk rate. Please try again.');
          // Revert to the previous rate
          setCustomRate(currentRate.toString());
        } finally {
          setSubmitting(false);
        }
      } else {
        // If invalid, revert to current rate
        setCustomRate(currentRate.toString());
      }
    }
    setIsEditingRate(!isEditingRate);
  };

  // Handle rate type toggle and update
  const toggleRateType = async () => {
    if (isEditingRateType) {
      try {
        setSubmittingRateType(true);

        // Prepare data for updating the dairy info
        const dairyData = {
          id: dairyInfo?.id,
          dairy_name: dairyInfo?.dairy_name || '',
          dairy_address: dairyInfo?.dairy_address || '',
          rate_type: rateType
        };

        // Call API to update the rate type
        await updateDairyInfo(dairyData);

        // Refresh dairy info after update
        const updatedDairyInfo = await getDairyInfo();
        setDairyInfo(updatedDairyInfo);

        // Update all collections based on the new rate type
        updateCollectionsForRateType(rateType);

        setSubmitSuccess(true);
        setCurrentSubmitMessage('Rate type updated successfully!');
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 3000);
      } catch (error) {
        console.error('Error updating rate type:', error);
        setSubmitError(error.error || 'Failed to update rate type. Please try again.');
      } finally {
        setSubmittingRateType(false);
        setIsEditingRateType(false);
      }
    } else {
      setIsEditingRateType(true);
    }
  };

  // Update all collections when rate type changes
  const updateCollectionsForRateType = (newRateType) => {
    if (collections.length === 0) return;

    const updatedCollections = collections.map(collection => {
      let updatedCollection = { ...collection };

      // If changing to fat_clr, calculate SNF from CLR if possible
      if (newRateType === 'fat_clr' && collection.fat_percent && collection.clr) {
        const calculatedSnf = calculateSnfFromClr(collection.clr, collection.fat_percent);
        updatedCollection.snf_percent = calculatedSnf.toString();
        updatedCollection.isSnfFromClr = true;
      }
      // If changing to fat_snf, keep SNF as is but remove isSnfFromClr flag
      else if (newRateType === 'fat_snf') {
        updatedCollection.isSnfFromClr = false;
      }

      // Recalculate derived values if we have the necessary data
      if (updatedCollection.fat_percent &&
        ((newRateType === 'fat_snf' && updatedCollection.snf_percent) ||
          (newRateType === 'fat_clr' && updatedCollection.clr)) &&
        updatedCollection.weight) {

        const derived = calculateDerivedValues(updatedCollection);
        updatedCollection = {
          ...updatedCollection,
          fat_kg: derived.fat_kg,
          snf_kg: derived.snf_kg,
          amount: derived.amount
        };
      }

      return updatedCollection;
    });

    setCollections(updatedCollections);
  };

  // Handle custom rate input change
  const handleRateChange = (e) => {
    setCustomRate(e.target.value);
  };

  // Handle rate type selection change
  const handleRateTypeChange = (e) => {
    const newRateType = e.target.value;
    setRateType(newRateType);

    // When rate type changes in the dropdown, update all collections accordingly
    updateCollectionsForRateType(newRateType);
  };

  // Handle fat/SNF ratio selection change
  const handleFatSnfRatioChange = (e) => {
    const newRatio = e.target.value;
    setFatSnfRatio(newRatio);

    // Update all collections with the new ratio if needed
    if (collections.length > 0) {
      updateCollectionAmountsWithProRata();
    }
  };

  // Update amounts in all collections when global rate changes
  const updateCollectionAmounts = (newRate) => {
    if (collections.length === 0) return;
    console.log('Updating collections with new rate:', newRate);

    const updatedCollections = collections.map(collection => {
      // For empty collections or ones that match the old rate, update to the new rate
      if (!collection.milk_rate || collection.milk_rate === currentRate || collection.milk_rate === '0') {
        const updatedCollection = {
          ...collection,
          milk_rate: newRate
        };

        // Only recalculate derived values if we have the required data
        if (updatedCollection.fat_percent && updatedCollection.snf_percent && updatedCollection.weight) {
          const derived = calculateDerivedValues(updatedCollection);
          return {
            ...updatedCollection,
            fat_kg: derived.fat_kg,
            snf_kg: derived.snf_kg,
            amount: derived.amount
          };
        }

        return updatedCollection;
      }
      return collection;
    });

    setCollections(updatedCollections);
  };

  // Handle creating a new customer - update to work with new search functionality
  const handleCreateCustomer = async () => {
    if (!newCustomer.name.trim()) {
      setSubmitError('Customer name is required');
      return;
    }

    try {
      setSubmitting(true);

      // Format phone number to remove any +91 prefix
      const formattedPhone = newCustomer.phone ? newCustomer.phone.replace(/^\+91/, '') : '';

      const customerData = {
        ...newCustomer,
        phone: formattedPhone
      };

      // Call API to create customer
      const response = await createCustomer(customerData);

      // Update customers list
      await fetchCustomers();

      // Select the new customer for the current collection
      if (response && currentCollection) {
        const index = collections.findIndex(c => c.id === currentCollection.id);
        if (index !== -1) {
          handleSelectCustomer(index, response);

          // Clear search query for this row
          setRowSearchQueries(prev => ({
            ...prev,
            [index]: ''
          }));

          // Ensure dropdown is hidden for this row
          setVisibleDropdowns(prev => ({
            ...prev,
            [index]: false
          }));
        }
      }

      // Reset form and close modal
      setNewCustomer({
        name: '',
        father_name: '',
        phone: '',
        village: '',
        address: '',
      });
      setShowNewCustomerModal(false);

      // Show success message
      setCurrentSubmitMessage(`New customer ${response.name} created successfully!`);
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error creating customer:', error);
      setSubmitError(error.error || 'Failed to create customer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle new customer form input changes
  const handleNewCustomerInputChange = (field, value) => {
    setNewCustomer({
      ...newCustomer,
      [field]: value
    });
  };

  /* eslint-disable no-unused-vars */
  // Handle global date change
  const handleGlobalDateChange = (e) => {
    const newDate = e.target.value;
    setGlobalDate(newDate);

    // No longer update existing collections - global date is only for new rows
  };

  // Handle global time change
  const handleGlobalTimeChange = (e) => {
    const newTime = e.target.value;
    setGlobalTime(newTime);

    // No longer update existing collections - global time is only for new rows
  };
  /* eslint-enable no-unused-vars */

  // Handle fat step up rate change
  const handleFatStepUpRateChange = (e) => {
    const newRate = e.target.value;
    setFatStepUpRate(newRate);

    // Update all collections with the new fat rate if needed
    if (collections.length > 0) {
      updateCollectionAmountsWithProRata();
    }
  };

  // Handle SNF step down rate change
  const handleSnfStepDownRateChange = (e) => {
    const newRate = e.target.value;
    setSnfStepDownRate(newRate);

    // Update all collections with the new SNF rate if needed
    if (collections.length > 0) {
      updateCollectionAmountsWithProRata();
    }
  };

  // Update all collections using the pro-rata calculation
  const updateCollectionAmountsWithProRata = () => {
    if (collections.length === 0) return;

    setCollections(prevCollections =>
      prevCollections.map(collection => {
        // Skip if we don't have both fat and SNF values
        if (!collection.fat_percent || (!collection.snf_percent && rateType === 'fat_snf')) {
          return collection;
        }

        // Recalculate derived values with the updated pro-rata rates
        const derived = calculateDerivedValues(collection);

        return {
          ...collection,
          fat_kg: derived.fat_kg,
          snf_kg: derived.snf_kg,
          amount: derived.amount
        };
      })
    );
  };

  if (loading || isLoadingRate) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="bulk-collection-v1-container">
      <header className="bulk-collection-header">
        <button className="back-button" onClick={goBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Dashboard</span>
        </button>
        <h1>Pro Rata Collection</h1>
      </header>

      {/* Dairy and Owner Information Section */}
      <div className="dairy-info-container">
        {loadingInfo ? (
          <div className="info-loading">Loading information...</div>
        ) : (
          <div className="dairy-info-content">
            <div className="dairy-details">
              <div className="info-item">
                <FontAwesomeIcon icon={faStore} className="info-icon" />
                <span className="info-label">Dairy Name:</span>
                <span className="info-value">{dairyInfo?.dairy_name || 'Not Available'}</span>
              </div>
              <div className="info-item">
                <FontAwesomeIcon icon={faWallet} className="info-icon" />
                <span className="info-label">Wallet Balance:</span>
                <span className="info-value">
                  {walletBalance !== null && walletBalance !== undefined
                    ? `₹${parseFloat(walletBalance).toFixed(2)}`
                    : 'Not Available'}
                </span>
              </div>
            </div>
            <div className="owner-details">
              <div className="info-item">
                <FontAwesomeIcon icon={faUser} className="info-icon" />
                <span className="info-label">Owner:</span>
                <span className="info-value">{userInfo?.name || 'Not Available'}</span>
              </div>
              <div className="info-item">
                <FontAwesomeIcon icon={faPhone} className="info-icon" />
                <span className="info-label">Phone:</span>
                <span className="info-value">{userInfo?.phone_number || 'Not Available'}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Low Balance Alert Popup */}
      {showLowBalanceAlert && (
        <div className="alert-popup-overlay">
          <div className="alert-popup">
            <div className="alert-popup-header">
              <FontAwesomeIcon icon={faExclamationCircle} className="alert-icon" />
              <h3>Low Wallet Balance</h3>
              <button
                className="close-alert-button"
                onClick={() => setShowLowBalanceAlert(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="alert-popup-body">
              <p>Your wallet balance is below ₹50. Please add funds to your wallet to ensure uninterrupted service.</p>
              <p className="alert-balance">Current Balance:
                <span className="low-balance-amount">
                  ₹{walletBalance !== null && walletBalance !== undefined
                    ? parseFloat(walletBalance).toFixed(2)
                    : '0.00'}
                </span>
              </p>
            </div>
            <div className="alert-popup-footer">
              <button
                className="close-alert-btn"
                onClick={() => setShowLowBalanceAlert(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bulk-collection-content">
        {submitSuccess && (
          <div className="success-message">
            <FontAwesomeIcon icon={faCheck} />
            <span>{currentSubmitMessage || 'Collections submitted successfully!'}</span>
          </div>
        )}

        {submitError && (
          <div className="error-message">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{submitError}</span>
            <button className="close-error" onClick={() => setSubmitError('')}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        <div className="current-rate-info">
          {isEditingRate ? (
            <div className="rate-edit">
              <label htmlFor="rate-input">Milk Rate (₹): </label>
              <input
                id="rate-input"
                type="number"
                step="0.1"
                min="0"
                value={customRate}
                onChange={handleRateChange}
                className="rate-input"
                autoFocus
                disabled={submitting}
              />
              <button
                type="button"
                onClick={toggleRateEdit}
                className="save-rate-button"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="rate-display">
              <p>Current Milk Rate: ₹{currentRate || '0'}</p>
              <button
                type="button"
                onClick={toggleRateEdit}
                className="edit-rate-button"
              >
                <FontAwesomeIcon icon={faEdit} />
                <span>Edit Rate</span>
              </button>
            </div>
          )}

          {/* Rate Type Edit Section */}
          <div className="rate-type-section">
            {isEditingRateType ? (
              <div className="rate-type-edit">
                <label htmlFor="rate-type-select">Rate Type: </label>
                <select
                  id="rate-type-select"
                  value={rateType}
                  onChange={handleRateTypeChange}
                  className="rate-type-select"
                  disabled={submittingRateType}
                >
                  <option value="fat_snf">FAT + SNF</option>
                  <option value="fat_clr">FAT + CLR</option>
                </select>
                <button
                  type="button"
                  onClick={toggleRateType}
                  className="save-rate-button"
                  disabled={submittingRateType}
                >
                  {submittingRateType ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} />
                      <span>Save</span>
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="rate-type-display">
                <p>Current Rate Type: {rateType === 'fat_snf' ? 'FAT + SNF' : 'FAT + CLR'}</p>
                <button
                  type="button"
                  onClick={toggleRateType}
                  className="edit-rate-button"
                >
                  <FontAwesomeIcon icon={faExchangeAlt} />
                  <span>Change Type</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Global settings section */}
        <div className="global-settings">
          <div className="global-settings-row">
            {/* Commented out default collection date 
            <div className="global-setting">
              <label>Default Collection Date (for new rows):</label>
              <div className="date-input-wrapper">
                <input
                  type="date"
                  value={globalDate}
                  onChange={handleGlobalDateChange}
                  className="global-input"
                  onClick={(e) => {
                    // Ensure event propagation and native date picker behavior
                    e.target.showPicker && e.target.showPicker();
                  }}
                />
              </div>
              <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#666' }}>
                This date will be used for new rows only
              </small>
            </div>
            */}

            {/* Commented out default collection time 
            <div className="global-setting">
              <label>Default Collection Time (for new rows):</label>
              <select
                value={globalTime}
                onChange={handleGlobalTimeChange}
                className="global-input"
              >
                <option value="morning">Morning</option>
                <option value="evening">Evening</option>
              </select>
              <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#666' }}>
                This time will be used for new rows only
              </small>
            </div>
            */}

            <div className="global-setting">
              <label>Fat Step Up Rate (₹):</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={fatStepUpRate}
                onChange={handleFatStepUpRateChange}
                className="global-input"
                placeholder="Enter fat step up rate"
              />
              <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#666' }}>
                Applied when Fat% &gt; 6.5
              </small>
            </div>

            <div className="global-setting">
              <label>SNF Step Down Rate (₹):</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={snfStepDownRate}
                onChange={handleSnfStepDownRateChange}
                className="global-input"
                placeholder="Enter SNF step down rate"
              />
              <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#666' }}>
                Applied with base SNF of 9.0
              </small>
            </div>

            <div className="global-setting">
              <label>Fat/SNF Ratio:</label>
              <select
                value={fatSnfRatio}
                onChange={handleFatSnfRatioChange}
                className="global-input"
              >
                <option value="60_40">60% Fat / 40% SNF (Default)</option>
                <option value="52_48">52% Fat / 48% SNF</option>
              </select>
              <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#666' }}>
                Ratio used to distribute milk rate between fat and SNF components
              </small>
            </div>

            <div className="global-setting">
              <label>Customer Management:</label>
              <button
                type="button"
                className="add-customer-button global-button"
                onClick={() => {
                  setCurrentCollection(null);
                  setShowNewCustomerModal(true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Add New Customer</span>
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handlePreview} noValidate>
          <div className="csv-grid-container">
            {/* Excel-like toolbar */}
            <div className="excel-toolbar">
              <div className="toolbar-left">
                <button
                  type="button"
                  className="add-row-button"
                  onClick={() => {
                    console.log('Add 1 Row button clicked');
                    addCollection(1);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Add 1 Row</span>
                </button>
                <button
                  type="button"
                  className="add-row-button"
                  onClick={() => {
                    console.log('Add 5 Rows button clicked');
                    addCollection(5);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Add 5 Rows</span>
                </button>
              </div>

              {collections.length > 0 && (
                <button
                  type="submit"
                  className="preview-button"
                  disabled={submitting}
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span>Preview & Submit</span>
                </button>
              )}
            </div>

            {/* True Excel-style table */}
            <table className="excel-table">
              <thead>
                <tr>
                  {/* S.No column */}
                  <th>#</th>
                  {/* Add date and time columns */}
                  <th>Date</th>
                  <th>Time</th>
                  <th>Customer</th>
                  <th>Weight (kg)</th>
                  <th>Fat %</th>
                  <th>SNF %</th>
                  <th>CLR</th>
                  <th>Milk Rate</th>
                  <th>Base SNF</th>
                  <th>Animal Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {collections.length === 0 ? (
                  <tr className="empty-row">
                    <td colSpan="12" className="empty-message">
                      No data yet. Click "Add Row" to begin adding collections.
                    </td>
                  </tr>
                ) : (
                  collections.map((collection, rowIndex) => (
                    <tr key={collection.id} className="data-row">
                      {/* Serial Number */}
                      <td className="serial-number-cell">
                        {rowIndex + 1}
                      </td>

                      {/* Date */}
                      <td>
                        <div className="date-input-wrapper">
                          <input
                            id={`collection_date-${rowIndex}`}
                            type="date"
                            value={collection.collection_date}
                            onChange={(e) => handleInputChange(rowIndex, 'collection_date', e.target.value)}
                            onFocus={() => handleCellFocus(rowIndex, 'collection_date')}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, 'collection_date')}
                            className="excel-cell-input date-input"
                            onClick={(e) => {
                              // Ensure event propagation and native date picker behavior
                              e.target.showPicker && e.target.showPicker();
                            }}
                          />
                        </div>
                      </td>

                      {/* Time */}
                      <td>
                        <select
                          id={`collection_time-${rowIndex}`}
                          value={collection.collection_time}
                          onChange={(e) => handleInputChange(rowIndex, 'collection_time', e.target.value)}
                          onFocus={() => handleCellFocus(rowIndex, 'collection_time')}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, 'collection_time')}
                          className="excel-cell-select"
                        >
                          <option value="morning">Morning</option>
                          <option value="evening">Evening</option>
                        </select>
                      </td>

                      {/* Customer */}
                      <td className={formErrors[collection.id]?.customer ? 'has-error' : ''}>
                        <div className="customer-search-cell">
                          <input
                            id={`customer-${rowIndex}`}
                            type="text"
                            value={
                              // If this row has an active search, show the search query
                              // Otherwise show the selected customer display name
                              (activeSearchRowIndex === rowIndex && rowSearchQueries[rowIndex] !== undefined) ?
                                rowSearchQueries[rowIndex] :
                                collection.customerDisplay || ''
                            }
                            onChange={(e) => handleSearch(rowIndex, e.target.value)}
                            onFocus={() => handleCellFocus(rowIndex, 'customer')}
                            onBlur={(e) => handleCellBlur(rowIndex, 'customer', e.target.value)}
                            placeholder="Search customer"
                            className="excel-cell-input"
                            ref={el => customerInputRefs.current[rowIndex] = el}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, 'customer')}
                          />
                          {/* Use row-specific dropdown visibility */}
                          {visibleDropdowns[rowIndex] && (
                            <div className="excel-dropdown">
                              {filteredCustomers.length > 0 ? (
                                <>
                                  {filteredCustomers.map((customer, index) => (
                                    <div
                                      key={customer.id}
                                      className={`dropdown-item ${selectedCustomerIndex === index ? 'selected' : ''}`}
                                      onMouseDown={(e) => {
                                        // Using onMouseDown instead of onClick prevents blur before selection
                                        e.preventDefault();
                                        handleSelectCustomer(rowIndex, customer);
                                      }}
                                      // Add hover handler to update selected index
                                      onMouseEnter={() => setSelectedCustomerIndex(index)}
                                    >
                                      {customer.customer_id} - {customer.name}
                                    </div>
                                  ))}
                                  <div
                                    className="dropdown-item add-new"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setCurrentCollection(collection);
                                      setShowNewCustomerModal(true);
                                      // Hide dropdown for this specific row
                                      setVisibleDropdowns(prev => ({
                                        ...prev,
                                        [rowIndex]: false
                                      }));
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faPlus} /> Add New Customer
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="no-results">No customers found</div>
                                  <div
                                    className="dropdown-item add-new"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setCurrentCollection(collection);
                                      setShowNewCustomerModal(true);
                                      // Hide dropdown for this specific row
                                      setVisibleDropdowns(prev => ({
                                        ...prev,
                                        [rowIndex]: false
                                      }));
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faPlus} /> Add New Customer
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                          {formErrors[collection.id]?.customer && (
                            <div className="cell-error">{formErrors[collection.id].customer}</div>
                          )}
                        </div>
                      </td>

                      {/* Weight (kg) */}
                      <td className={formErrors[collection.id]?.weight ? 'has-error' : ''}>
                        <input
                          id={`weight-${rowIndex}`}
                          type="number"
                          step="0.1"
                          min="0"
                          value={collection.weight}
                          onChange={(e) => handleInputChange(rowIndex, 'weight', e.target.value)}
                          onFocus={() => handleCellFocus(rowIndex, 'weight')}
                          onBlur={(e) => handleCellBlur(rowIndex, 'weight', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, 'weight')}
                          placeholder="e.g. 10.5"
                          className="excel-cell-input"
                        />
                        {formErrors[collection.id]?.weight && (
                          <div className="cell-error">{formErrors[collection.id].weight}</div>
                        )}
                      </td>

                      {/* Fat % */}
                      <td className={formErrors[collection.id]?.fat_percent ? 'has-error' : ''}>
                        <input
                          id={`fat_percent-${rowIndex}`}
                          type="text"
                          value={collection.fat_percent}
                          onChange={(e) => handleInputChange(rowIndex, 'fat_percent', e.target.value)}
                          onFocus={() => handleCellFocus(rowIndex, 'fat_percent')}
                          onBlur={(e) => handleCellBlur(rowIndex, 'fat_percent', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, 'fat_percent')}
                          placeholder="e.g. 35 ➞ 3.5"
                          className="excel-cell-input"
                        />
                        {formErrors[collection.id]?.fat_percent && (
                          <div className="cell-error">{formErrors[collection.id].fat_percent}</div>
                        )}
                        {collection.fat_percent && parseFloat(collection.fat_percent) < 2.0 && !formErrors[collection.id]?.fat_percent && (
                          <div className="cell-warning">Warning: Fat % below minimum 2.0%</div>
                        )}
                        {collection.fat_percent && parseFloat(collection.fat_percent) > 13.1 && (
                          <div className="cell-warning">Warning: Fat % exceeds 13.1%</div>
                        )}
                      </td>

                      {/* SNF % */}
                      <td className={formErrors[collection.id]?.snf_percent ? 'has-error' : ''}>
                        <div className={collection.isSnfFromClr || rateType === 'fat_clr' ? 'calculated-cell' : ''}>
                          <input
                            id={`snf_percent-${rowIndex}`}
                            type="text"
                            value={collection.snf_percent}
                            onChange={(e) => handleInputChange(rowIndex, 'snf_percent', e.target.value)}
                            onFocus={() => handleCellFocus(rowIndex, 'snf_percent')}
                            onBlur={(e) => handleCellBlur(rowIndex, 'snf_percent', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, 'snf_percent')}
                            placeholder="e.g. 85 ➞ 8.5"
                            className={`excel-cell-input ${rateType === 'fat_clr' ? 'auto-calculated' : ''}`}
                            readOnly={collection.isSnfFromClr || rateType === 'fat_clr'}
                            disabled={rateType === 'fat_clr'}
                          />
                          {collection.isSnfFromClr && <span className="calc-indicator">*</span>}
                        </div>
                        {formErrors[collection.id]?.snf_percent && (
                          <div className="cell-error">{formErrors[collection.id].snf_percent}</div>
                        )}
                        {collection.snf_percent && parseFloat(collection.snf_percent) < 4.0 && !formErrors[collection.id]?.snf_percent && rateType !== 'fat_clr' && (
                          <div className="cell-warning">Warning: SNF % below minimum 4.0%</div>
                        )}
                        {collection.snf_percent && parseFloat(collection.snf_percent) > 11.0 && (
                          <div className="cell-warning">Warning: SNF % exceeds 11.0%</div>
                        )}
                      </td>

                      {/* CLR */}
                      <td className={formErrors[collection.id]?.clr ? 'has-error' : ''}>
                        <div className={rateType === 'fat_snf' ? 'disabled-field' : ''}>
                          <input
                            id={`clr-${rowIndex}`}
                            type="text"
                            value={collection.clr}
                            onChange={(e) => handleInputChange(rowIndex, 'clr', e.target.value)}
                            onFocus={() => handleCellFocus(rowIndex, 'clr')}
                            onBlur={(e) => handleCellBlur(rowIndex, 'clr', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, 'clr')}
                            placeholder="e.g. 275 ➞ 27.5"
                            className={`excel-cell-input ${rateType === 'fat_snf' ? 'disabled-input' : ''}`}
                            disabled={rateType === 'fat_snf'}
                            readOnly={rateType === 'fat_snf'}
                          />
                          {rateType === 'fat_snf' && <span className="disabled-indicator">N/A</span>}
                        </div>
                        {formErrors[collection.id]?.clr && (
                          <div className="cell-error">{formErrors[collection.id].clr}</div>
                        )}
                      </td>

                      {/* Milk Rate */}
                      <td>
                        {/* Using milk_rate: {collection.milk_rate}, currentRate: {currentRate} */}
                        <input
                          id={`milk_rate-${rowIndex}`}
                          type="number"
                          step="0.1"
                          min="0"
                          value={collection.milk_rate || ''}
                          onChange={(e) => handleInputChange(rowIndex, 'milk_rate', e.target.value)}
                          onFocus={() => handleCellFocus(rowIndex, 'milk_rate')}
                          onBlur={(e) => handleCellBlur(rowIndex, 'milk_rate', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, 'milk_rate')}
                          placeholder="Rate"
                          className="excel-cell-input"
                        />
                      </td>

                      {/* Base SNF */}
                      <td>
                        <select
                          id={`base_snf-${rowIndex}`}
                          value={collection.base_snf}
                          onChange={(e) => handleInputChange(rowIndex, 'base_snf', e.target.value)}
                          onFocus={() => handleCellFocus(rowIndex, 'base_snf')}
                          onBlur={(e) => handleCellBlur(rowIndex, 'base_snf', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, 'base_snf')}
                          className="excel-cell-select"
                        >
                          <option value="9.0">9.0</option>
                          <option value="9.1">9.1</option>
                          <option value="9.2">9.2</option>
                          <option value="9.3">9.3</option>
                          <option value="9.4">9.4</option>
                          <option value="9.5">9.5</option>
                        </select>
                      </td>

                      {/* Animal Type */}
                      <td>
                        <select
                          id={`animal_type-${rowIndex}`}
                          value={collection.animal_type}
                          onChange={(e) => handleInputChange(rowIndex, 'animal_type', e.target.value)}
                          onFocus={() => handleCellFocus(rowIndex, 'animal_type')}
                          onBlur={(e) => handleCellBlur(rowIndex, 'animal_type', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, 'animal_type')}
                          className="excel-cell-select"
                        >
                          <option value="cow">Cow</option>
                          <option value="buffalo">Buffalo</option>
                          <option value="cow+buffalo">Cow+Buffalo</option>
                        </select>
                      </td>

                      {/* Actions */}
                      <td>
                        <button
                          type="button"
                          className="delete-row-btn"
                          onClick={() => handleDeleteClick(collection)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {collections.length > 0 && (
                <tfoot>
                  <tr className="total-row">
                    <td colSpan="2" className="total-label">Total Weight:</td>
                    <td className="total-value weight-total" colSpan="2">
                      {collections.reduce((sum, c) => sum + (parseFloat(c.weight) || 0), 0).toFixed(2)} kg
                    </td>
                    <td colSpan="2" className="total-label">Total Records:</td>
                    <td className="total-value" colSpan="3">
                      {collections.filter(c => rowHasData(c)).length}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </form>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="modal-overlay">
          <div className="preview-modal">
            <div className="modal-header">
              <h2>Preview Collections</h2>
              <button
                className="close-modal-button"
                onClick={() => setShowPreviewModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              <div className="preview-header">
                <p className="preview-info">Please review the following collections before submitting:</p>
              </div>

              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Customer</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Weight</th>
                      <th>Fat %</th>
                      <th>CLR</th>
                      <th>SNF %</th>
                      <th>Fat KG</th>
                      <th>SNF KG</th>
                      <th>Rate</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewCollections.map((collection, index) => {
                      // eslint-disable-next-line no-unused-vars
                      const fat = parseFloat(collection.fat_percentage) || 0;
                      // eslint-disable-next-line no-unused-vars
                      const snf = parseFloat(collection.snf_percentage) || 0;
                      // Determine if pro-rata applies
                      const isProRata = collection.is_pro_rata;

                      return (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{collections[index].customerDisplay}</td>
                          <td>{collection.collection_date}</td>
                          <td>{collection.collection_time === 'morning' ? 'Morning' : 'Evening'}</td>
                          <td>{parseFloat(collection.weight).toFixed(2)} kg</td>
                          <td>
                            {parseFloat(collection.fat_percentage).toFixed(2)}%
                            {isProRata && (
                              <span style={{
                                color: '#0066cc',
                                fontWeight: 'bold',
                                marginLeft: '3px',
                                fontSize: '11px',
                              }}>*</span>
                            )}
                          </td>
                          <td>{collection.clr ? parseFloat(collection.clr).toFixed(1) : '-'}</td>
                          <td>{parseFloat(collection.snf_percentage).toFixed(2)}%</td>
                          <td>{parseFloat(collection.fat_kg).toFixed(2)} kg</td>
                          <td>{parseFloat(collection.snf_kg).toFixed(2)} kg</td>
                          <td>
                            {isProRata ? (
                              <div>
                                <span style={{ color: '#0066cc', fontWeight: 'bold' }}>
                                  ₹{parseFloat(collection.effective_rate).toFixed(2)}
                                </span>
                              </div>
                            ) : (
                              <>₹{parseFloat(collection.milk_rate).toFixed(2)}</>
                            )}
                          </td>
                          <td>₹{Math.round(parseFloat(collection.amount))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="11" className="total-label">Total Amount:</td>
                      <td className="total-value">
                        ₹{Math.round(previewCollections.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0))}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="11" className="total-label">Total Weight:</td>
                      <td className="total-value">
                        {previewCollections.reduce((sum, c) => sum + (parseFloat(c.weight) || 0), 0).toFixed(2)} kg
                      </td>
                    </tr>
                  </tfoot>
                </table>
                {previewCollections.some(c => c.is_pro_rata) && (
                  <div style={{
                    margin: '10px 0',
                    padding: '8px 12px',
                    backgroundColor: '#e8f4ff',
                    borderLeft: '3px solid #0066cc',
                    fontSize: '13px',
                    color: '#0066cc'
                  }}>
                    <strong>Note:</strong> * Pro-rata calculation applied where Fat% &gt; 6.5. Rate formula: Base Rate + ((Fat%-6.5)×{parseFloat(fatStepUpRate).toFixed(2)}) + ((SNF%-9)×{parseFloat(snfStepDownRate).toFixed(2)})
                  </div>
                )}

                <div style={{
                  margin: '10px 0',
                  padding: '8px 12px',
                  backgroundColor: '#f0f7ff',
                  borderLeft: '3px solid #4d90fe',
                  fontSize: '13px',
                  color: '#333'
                }}>
                  <strong>Fat/SNF Ratio:</strong> {fatSnfRatio === '60_40' ? '60% Fat / 40% SNF' : '52% Fat / 48% SNF'} -
                  Used for calculating Fat Rate ({fatSnfRatio === '60_40' ? '60' : '52'}% of rate / 6.5) and
                  SNF Rate ({fatSnfRatio === '60_40' ? '40' : '48'}% of rate / base SNF)
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowPreviewModal(false)}
              >
                Cancel
              </button>
              <button
                className="confirm-button"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Confirm & Submit</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Customer Modal */}
      {showNewCustomerModal && (
        <div className="modal-overlay">
          <div className="new-customer-modal">
            <div className="modal-header">
              <h2>Add New Customer</h2>
              <button
                className="close-modal-button"
                onClick={() => setShowNewCustomerModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              <p className="form-instructions">Please fill in the customer details. Fields marked with * are required.</p>

              <div className="form-group">
                <label htmlFor="customer-name">Name</label>
                <input
                  id="customer-name"
                  type="text"
                  value={newCustomer.name}
                  onChange={(e) => handleNewCustomerInputChange('name', e.target.value)}
                  placeholder="Enter customer name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="father-name">Father's Name</label>
                <input
                  id="father-name"
                  type="text"
                  value={newCustomer.father_name}
                  onChange={(e) => handleNewCustomerInputChange('father_name', e.target.value)}
                  placeholder="Enter father's name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  type="tel"
                  value={newCustomer.phone}
                  onChange={(e) => handleNewCustomerInputChange('phone', e.target.value)}
                  placeholder="Enter 10-digit phone number"
                  maxLength={10}
                />
              </div>

              <div className="form-group">
                <label htmlFor="village">Village</label>
                <input
                  id="village"
                  type="text"
                  value={newCustomer.village}
                  onChange={(e) => handleNewCustomerInputChange('village', e.target.value)}
                  placeholder="Enter village name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  value={newCustomer.address}
                  onChange={(e) => handleNewCustomerInputChange('address', e.target.value)}
                  placeholder="Enter complete address"
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowNewCustomerModal(false)}
              >
                Cancel
              </button>
              <button
                className="save-button"
                onClick={handleCreateCustomer}
                disabled={submitting || !newCustomer.name.trim()}
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    <span>Save Customer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submission Loading Overlay */}
      {submitting && !showPreviewModal && !showNewCustomerModal && (
        <div className="submission-overlay">
          <div className="submission-spinner"></div>
          <div className="submission-message">Submitting collections to server...</div>
          {submissionTotal > 0 && (
            <div className="submission-progress">
              Progress: {submissionProgress} of {submissionTotal} collections
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="delete-confirmation-modal">
            <div className="warning-header">
              <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
              <h2>Delete Collection Row</h2>
              <button
                className="close-modal-button"
                onClick={() => setShowDeleteConfirmation(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>


            <div className="modal-body">
              <div className="confirmation-icon-container">
                <FontAwesomeIcon icon={faTrash} size="lg" style={{ color: '#e74c3c' }} />
              </div>
              <p className="warning-text">This row contains data that will be permanently deleted</p>
              <p>Are you sure you want to proceed with deletion?</p>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowDeleteConfirmation(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>Cancel</span>
              </button>
              <button
                className="delete-button"
                onClick={() => {
                  removeCollection(rowToDelete);
                  setShowDeleteConfirmation(false);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProRataCollection; 
